import React, { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { VideoCard } from "../components/VideoCard";
import { Button } from "../components/ui/button";
import { UserPlus, Users, TrendingUp, BadgeCheck, ShieldAlert, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

export default function Home() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/home").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const follow = async (id) => {
    try {
      await api.post(`/users/${id}/follow`);
      toast.success("Following");
      // Remove the followed user from the suggested list locally (no full reload)
      setData((prev) => prev ? {
        ...prev,
        suggested_users: prev.suggested_users.filter((u) => u.id !== id),
      } : prev);
    } catch { toast.error("Failed to follow"); }
  };

  const joinGroup = async (id) => {
    try {
      await api.post(`/groups/${id}/join`);
      toast.success("Joined group");
      setData((prev) => prev ? {
        ...prev,
        suggested_groups: prev.suggested_groups.filter((g) => g.id !== id),
      } : prev);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-10 text-muted-foreground">Loading feed…</div>;
  if (!data) return null;

  // Merge debates + thoughts sorted by date for main feed
  const feedItems = [
    ...data.recent_debates.map((d) => ({ ...d, _type: "debate" })),
    ...data.recent_thoughts.map((t) => ({ ...t, _type: "thought" })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <div className="max-w-[1080px] mx-auto px-4 lg:px-8 py-6">
      {/* Stories bar */}
      <section className="mb-6" data-testid="stories-bar">
        <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-3">
          {/* Your story - create button */}
          <Link to="/create" className="flex flex-col items-center gap-2 flex-shrink-0 group" data-testid="story-create">
            <div className="w-16 h-16 rounded-full bg-muted border-2 border-dashed border-[hsl(var(--primary))] flex items-center justify-center group-hover:bg-[hsl(var(--primary-soft))] transition-colors">
              <Plus className="w-6 h-6 text-[hsl(var(--primary))]" />
            </div>
            <span className="text-xs text-muted-foreground">Your post</span>
          </Link>

          {data.suggested_users.slice(0, 12).map((u) => (
            <Link
              key={u.id}
              to={`/profile/${u.username}`}
              className="flex flex-col items-center gap-2 flex-shrink-0 group"
              data-testid={`story-${u.username}`}
            >
              <div className="story-ring rounded-full">
                <div className="w-16 h-16 rounded-full bg-card border-2 border-card flex items-center justify-center overflow-hidden">
                  {u.profile_photo ? (
                    <img src={fileUrl(u.profile_photo)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-bold text-lg text-[hsl(var(--primary))]">
                      {u.username?.[0]?.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              <span className="text-xs text-muted-foreground max-w-[70px] truncate">@{u.username}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Verification banner */}
      {user?.verification_status !== "verified" && (
        <div className="mb-6 border border-[hsl(var(--primary))] bg-[hsl(var(--primary-soft))] rounded-2xl p-5 flex flex-wrap gap-4 items-center justify-between" data-testid="home-verify-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))] text-white flex items-center justify-center">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold text-sm">Verify your identity to start posting</div>
              <p className="text-xs text-muted-foreground">Aadhaar verification unlocks debates &amp; thoughts.</p>
            </div>
          </div>
          <Link to="/verify">
            <Button className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="home-verify-cta">
              Verify identity
            </Button>
          </Link>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Main feed - Instagram style single column */}
        <div className="lg:col-span-8 max-w-[600px] mx-auto lg:mx-0 w-full space-y-6">
          {feedItems.length === 0 ? (
            <div className="border border-dashed border-border rounded-2xl p-16 text-center">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--primary-soft))] mx-auto mb-4 flex items-center justify-center">
                <Plus className="w-8 h-8 text-[hsl(var(--primary))]" />
              </div>
              <h3 className="font-serif text-2xl mb-2">Feed is empty</h3>
              <p className="text-sm text-muted-foreground mb-6">Follow people or be the first to post</p>
              <Link to="/create">
                <Button className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="empty-feed-create">
                  Create the first post
                </Button>
              </Link>
            </div>
          ) : (
            feedItems.map((item) => (
              <VideoCard key={`${item._type}-${item.id}`} item={item} type={item._type} />
            ))
          )}
        </div>

        {/* Right sidebar - suggestions (desktop only) */}
        <aside className="hidden lg:block lg:col-span-4 space-y-6 sticky top-6 self-start">
          {/* Me card */}
          <div className="flex items-center gap-3 px-1">
            <Link to={`/profile/${user?.username}`} className="w-14 h-14 rounded-full bg-[hsl(var(--primary-soft))] border border-border flex items-center justify-center overflow-hidden">
              {user?.profile_photo ? (
                <img src={fileUrl(user.profile_photo)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="font-bold text-lg text-[hsl(var(--primary))]">{user?.username?.[0]?.toUpperCase()}</span>
              )}
            </Link>
            <div className="flex-1 min-w-0">
              <Link to={`/profile/${user?.username}`} className="font-semibold text-sm hover:text-[hsl(var(--primary))]" data-testid="sidebar-me-link">
                @{user?.username}
              </Link>
              <div className="text-xs text-muted-foreground truncate">{user?.full_name}</div>
            </div>
            {user?.verification_status === "verified" && (
              <BadgeCheck className="w-5 h-5 fill-[hsl(var(--primary))] text-white" />
            )}
          </div>

          {/* Suggested users */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-muted-foreground">Suggested for you</div>
              <Link to="/search" className="text-xs font-semibold hover:text-[hsl(var(--primary))]" data-testid="see-all-users">
                See all
              </Link>
            </div>
            <div className="space-y-3">
              {data.suggested_users.slice(0, 5).map((u) => (
                <div key={u.id} className="flex items-center gap-3" data-testid={`suggested-user-${u.username}`}>
                  <Link to={`/profile/${u.username}`} className="w-11 h-11 rounded-full bg-[hsl(var(--primary-soft))] border border-border flex items-center justify-center overflow-hidden flex-shrink-0">
                    {u.profile_photo ? (
                      <img src={fileUrl(u.profile_photo)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-bold text-[hsl(var(--primary))]">{u.username?.[0]?.toUpperCase()}</span>
                    )}
                  </Link>
                  <Link to={`/profile/${u.username}`} className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-1 truncate hover:text-[hsl(var(--primary))]">
                      @{u.username}
                      {u.verification_status === "verified" && <BadgeCheck className="w-3.5 h-3.5 fill-[hsl(var(--primary))] text-white flex-shrink-0" />}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{u.followers_count} followers</div>
                  </Link>
                  <button
                    onClick={() => follow(u.id)}
                    className="text-xs font-semibold text-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]/70 transition-colors"
                    data-testid={`follow-btn-${u.username}`}
                  >
                    Follow
                  </button>
                </div>
              ))}
              {data.suggested_users.length === 0 && (
                <div className="text-xs text-muted-foreground py-3">No suggestions yet</div>
              )}
            </div>
          </section>

          {/* Suggested groups */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-muted-foreground">Discover groups</div>
              <Link to="/groups" className="text-xs font-semibold hover:text-[hsl(var(--primary))]" data-testid="see-all-groups">
                See all
              </Link>
            </div>
            <div className="space-y-3">
              {data.suggested_groups.slice(0, 4).map((g) => (
                <div key={g.id} className="flex items-center gap-3" data-testid={`suggested-group-${g.id}`}>
                  <Link to={`/groups/${g.id}`} className="w-11 h-11 rounded-2xl bg-[hsl(var(--primary-soft))] flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[hsl(var(--primary))]" />
                  </Link>
                  <Link to={`/groups/${g.id}`} className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate hover:text-[hsl(var(--primary))]">{g.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{g.members_count} members</div>
                  </Link>
                  <button
                    onClick={() => joinGroup(g.id)}
                    className="text-xs font-semibold text-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]/70 transition-colors"
                    data-testid={`join-group-${g.id}`}
                  >
                    Join
                  </button>
                </div>
              ))}
              {data.suggested_groups.length === 0 && (
                <div className="text-xs text-muted-foreground py-3">No groups yet</div>
              )}
            </div>
          </section>

          {/* Trending debates */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-[hsl(var(--primary))]" />
              <div className="text-sm font-semibold text-muted-foreground">Trending debates</div>
            </div>
            <div className="space-y-3">
              {data.trending_debates.slice(0, 4).map((d, i) => (
                <Link key={d.id} to={`/debates/${d.id}`} className="flex gap-3 hover:bg-muted p-2 -mx-2 rounded-lg transition-colors group" data-testid={`trending-debate-${d.id}`}>
                  <div className="font-serif text-2xl text-muted-foreground/40 w-6 flex-shrink-0">{String(i + 1).padStart(2, "0")}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm line-clamp-2 group-hover:text-[hsl(var(--primary))] transition-colors">{d.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      @{d.author?.username} · {d.views} views
                    </div>
                  </div>
                </Link>
              ))}
              {data.trending_debates.length === 0 && (
                <div className="text-xs text-muted-foreground py-3">No trending debates yet</div>
              )}
            </div>
          </section>

          <div className="text-[10px] text-muted-foreground pt-4 border-t border-border">
            © 2026 TRUEBATE · Where every opinion has a voice
          </div>
        </aside>
      </div>
    </div>
  );
}
